// AJUSTES DE REFERIDOS (F3 reorden 24-oct-2026). Antes este bloque vivía dentro de Mi Negocio → Programa (y desde F1 en
// «Cómo premio»), lejos del historial y del mensaje de referidos. Ahora es la pestaña «Ajustes» de Referidos: encender,
// cuánto gana quien recomienda y el bono del nuevo. Guarda SOLO estas tres claves con updateBusinessInfo (parcial →
// PUT /loyalty/settings), sin tocar el resto de la configuración del negocio.
import React, { useEffect, useState } from 'react';
import NumInput from '../../../components/ui/NumInput';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Save, Check, Loader2, Crown, Info, AlertTriangle, ToggleLeft, ToggleRight, Users } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';

export const REFERRAL_PRESETS = [0, 1, 5, 10, 20, 50, 100];
const esPreset = (v: number) => REFERRAL_PRESETS.includes(v);

const PresetSelector: React.FC<{
  label: string;
  description: string;
  value: number;
  onChange: (val: number) => void;
  showCustom: boolean;
  setShowCustom: (show: boolean) => void;
  cashback: boolean;
  unitPrefix: string;
  testid: string;
}> = ({ label, description, value, onChange, showCustom, setShowCustom, cashback, unitPrefix, testid }) => (
  <div data-testid={testid}>
    <label className="block text-sm font-medium text-content-2 mb-2">{label}</label>
    <div className="flex flex-wrap gap-2 mb-2">
      {REFERRAL_PRESETS.map((preset) => (
        <button
          key={preset}
          type="button"
          onClick={() => { onChange(preset); setShowCustom(false); }}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
            !showCustom && value === preset
              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500'
              : 'border-line bg-surface text-content-2 hover:border-emerald-300 dark:hover:border-emerald-500/40 hover:bg-emerald-50 dark:hover:bg-emerald-500/10'
          }`}
        >
          {preset === 0 ? 'Ninguno' : `${unitPrefix}${preset}`}
        </button>
      ))}
      <button
        type="button"
        onClick={() => { setShowCustom(true); if (esPreset(value)) onChange(value || 0); }}
        className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
          showCustom
            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500'
            : 'border-line bg-surface text-content-2 hover:border-emerald-300 dark:hover:border-emerald-500/40 hover:bg-emerald-50 dark:hover:bg-emerald-500/10'
        }`}
      >
        Personalizado
      </button>
    </div>
    {showCustom && (
      <div className="mt-2 relative rounded-md shadow-sm max-w-xs animate-fade-in-up">
        {cashback && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-content-3 sm:text-sm">$</span>
          </div>
        )}
        <NumInput
          min="0"
          value={value}
          onValue={onChange}
          className={`focus:ring-emerald-500 focus:border-emerald-500 block w-full ${cashback ? 'pl-7' : 'pl-3'} pr-3 sm:text-sm border-line rounded-md bg-surface text-content`}
          placeholder="Ingresa cantidad"
        />
      </div>
    )}
    <p className="mt-1 text-xs text-content-3">{description}</p>
  </div>
);

const ReferralSettingsCard: React.FC<{ hasPremiumAccess: boolean }> = ({ hasPremiumAccess }) => {
  const { currentUser, updateBusinessInfo } = useAuth();
  const { handleFirebaseError } = useError();
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';
  const cashback = loyaltyMode === 'cashback';
  const unitLabel = cashback ? 'pesos' : 'puntos';
  const unitPrefix = cashback ? '$' : '';
  const allowPointsAccumulation = currentUser?.allowPointsAccumulation !== false;
  const pointsPerVisit = currentUser?.pointsPerVisit ?? 1;

  const [referralsEnabled, setReferralsEnabled] = useState(currentUser?.referralsEnabled || false);
  const [pointsPerReferral, setPointsPerReferral] = useState<number>(currentUser?.pointsPerReferral ?? 0);
  const [referralBonusForNewClient, setReferralBonusForNewClient] = useState<number>(currentUser?.referralBonusForNewClient ?? 0);
  const [showCustomPoints, setShowCustomPoints] = useState(!esPreset(currentUser?.pointsPerReferral ?? 0));
  const [showCustomBonus, setShowCustomBonus] = useState(!esPreset(currentUser?.referralBonusForNewClient ?? 0));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Si el usuario llega de recargar (currentUser tarda en llenarse), sincroniza una vez con lo guardado.
  useEffect(() => {
    if (!currentUser) return;
    setReferralsEnabled(currentUser.referralsEnabled || false);
    setPointsPerReferral(currentUser.pointsPerReferral ?? 0);
    setReferralBonusForNewClient(currentUser.referralBonusForNewClient ?? 0);
    setShowCustomPoints(!esPreset(currentUser.pointsPerReferral ?? 0));
    setShowCustomBonus(!esPreset(currentUser.referralBonusForNewClient ?? 0));
  }, [currentUser?.referralsEnabled, currentUser?.pointsPerReferral, currentUser?.referralBonusForNewClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = referralsEnabled !== (currentUser?.referralsEnabled || false)
    || pointsPerReferral !== (currentUser?.pointsPerReferral ?? 0)
    || referralBonusForNewClient !== (currentUser?.referralBonusForNewClient ?? 0);

  const guardar = async () => {
    if (!currentUser?.businessId) return;
    setSaving(true);
    try {
      await updateBusinessInfo(currentUser.businessId, { referralsEnabled, pointsPerReferral, referralBonusForNewClient });
      setSaved(true); toast.success('Referidos guardados'); setTimeout(() => setSaved(false), 2000);
    } catch (e) { handleFirebaseError(e, 'referrals-settings-save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-surface p-5 rounded-lg border border-line shadow-sm animate-fade-in-up" data-testid="referidos-ajustes">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Users className="h-5 w-5 text-emerald-600 mr-2" />
          <h3 className="text-lg font-medium text-content">Cómo premias a quien recomienda</h3>
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-500/40">
            <Crown className="w-3 h-3 mr-1 text-purple-500" /> PRO
          </span>
        </div>
      </div>

      {!hasPremiumAccess ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-content-2">
            Premia con {unitLabel} a quien recomienda tu negocio y dale un bonus de bienvenida al nuevo cliente. Todo automático, en kiosco y registro por QR.
          </p>
          <RouterLink
            to="/dashboard/subscription"
            className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md"
          >
            <Crown className="h-4 w-4 mr-2" />
            Conocer más
          </RouterLink>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-surface-2 rounded-lg">
            <div>
              <span className="text-sm font-medium text-content">Activar sistema de referidos</span>
              <p className="text-xs text-content-3 mt-0.5">Al registrarse, los nuevos clientes podrán indicar quién los refirió</p>
            </div>
            <button type="button" onClick={() => setReferralsEnabled(!referralsEnabled)} className="flex-shrink-0" data-testid="referidos-toggle" aria-pressed={referralsEnabled}>
              {referralsEnabled ? <ToggleRight className="h-8 w-8 text-emerald-600" /> : <ToggleLeft className="h-8 w-8 text-content-3" />}
            </button>
          </div>

          {referralsEnabled && (
            <div className="space-y-5 animate-fade-in-up">
              <PresetSelector
                label={cashback ? 'Saldo para quien refiere ($MXN)' : 'Puntos para quien refiere'}
                description={cashback ? 'Saldo que recibe el cliente que refirió cuando el nuevo se registra' : 'Puntos que recibe el cliente que refirió cuando el nuevo se registra'}
                value={pointsPerReferral} onChange={setPointsPerReferral}
                showCustom={showCustomPoints} setShowCustom={setShowCustomPoints}
                cashback={cashback} unitPrefix={unitPrefix} testid="referidos-puntos"
              />
              <PresetSelector
                label={cashback ? 'Bonus de bienvenida al referido ($MXN)' : 'Bonus de bienvenida al referido (puntos)'}
                description="Opcional — el nuevo cliente recibe este bonus adicional a los puntos normales de su primera visita"
                value={referralBonusForNewClient} onChange={setReferralBonusForNewClient}
                showCustom={showCustomBonus} setShowCustom={setShowCustomBonus}
                cashback={cashback} unitPrefix={unitPrefix} testid="referidos-bonus"
              />

              <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Info className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Vista previa del flujo</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-200 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center text-xs font-bold">1</div>
                    <p className="ml-3 text-sm text-emerald-700 dark:text-emerald-300"><strong>María</strong> (cliente existente) le recomienda tu negocio a <strong>Juan</strong>.</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-200 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center text-xs font-bold">2</div>
                    <p className="ml-3 text-sm text-emerald-700 dark:text-emerald-300"><strong>Juan</strong> se registra en el kiosco e ingresa el teléfono de María como referidor.</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-200 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center text-xs font-bold">3</div>
                    <div className="ml-3 text-sm text-emerald-700 dark:text-emerald-300">
                      <p className="font-medium mb-1">Resultado automático:</p>
                      <div className="space-y-1 ml-2">
                        {pointsPerReferral > 0 ? (
                          <p>✅ <strong>María</strong> recibe <span className="font-bold text-emerald-800 dark:text-emerald-200">{unitPrefix}{pointsPerReferral} {unitLabel}</span> de recompensa por referir.</p>
                        ) : (
                          <p className="text-emerald-500/70">— María no recibe recompensa (selecciona una cantidad arriba).</p>
                        )}
                        {referralBonusForNewClient > 0 ? (
                          <p>🎁 <strong>Juan</strong> recibe <span className="font-bold text-emerald-800 dark:text-emerald-200">{unitPrefix}{referralBonusForNewClient} {unitLabel}</span> de bonus de bienvenida
                            {allowPointsAccumulation
                              ? <>, adicional a {cashback ? 'su cashback' : <>sus <strong>{pointsPerVisit} {unitLabel}</strong></>} por la visita.</>
                              : <>.</>}
                          </p>
                        ) : (
                          <p className="text-emerald-500/70">— Juan no recibe bonus extra
                            {allowPointsAccumulation
                              ? <> (solo {cashback ? 'su cashback' : <>sus <strong>{pointsPerVisit} {unitLabel}</strong></>} normales).</>
                              : <>.</>}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {!allowPointsAccumulation && (pointsPerReferral > 0 || referralBonusForNewClient > 0) && (
                  <div className="mt-3 pt-3 border-t border-emerald-200 dark:border-emerald-500/30">
                    <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start">
                      <AlertTriangle className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                      Tienes la acumulación de {unitLabel} desactivada, pero los bonos por referido sí se entregarán como incentivo especial de marketing.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-line">
            {saved && <span className="inline-flex items-center text-sm text-emerald-600"><Check className="h-4 w-4 mr-1" /> Guardado</span>}
            <button
              type="button"
              onClick={guardar}
              disabled={saving || !isDirty}
              data-testid="referidos-guardar"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar referidos
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralSettingsCard;
